/* ============================================================
   Trasse Families — the car, while it is out.

   The design decision this component encodes, carried over verbatim from the
   phone's Today screen: BEFORE THE RUN STARTS THERE IS NOTHING TO TRACK. A map
   of a parked car is an anxiety generator — it invites a parent to stare at a
   stationary dot and draw conclusions from it. So the map exists only while a
   run is genuinely open, and the rest of the time this panel is a sentence.

   The second rule is about the dot itself. A GPS fix has an age, and a stale
   one drawn as a confident marker is the app claiming to know where a car is
   when it knows where the car WAS. Past a threshold the panel says so and stops
   pretending: "last seen 14 minutes ago" is true; a live-looking dot is not.
   ============================================================ */
import React, { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { GoogleMap, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { db } from '../../firebase';
import { materializeBusDoc } from '../../utils/locationCrypto';
import {
  GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID, GOOGLE_MAPS_VERSION,
} from '../../utils/googleMapsLoader';
import { FAM } from './familyTheme';

/** Statuses that mean a run is genuinely open. Anything else — including the
 *  refuel-trip status, which is deliberately not 'active' — is not a school run
 *  and must never put a live map in front of a parent. */
const OPEN_STATUSES = new Set(['active', 'collecting']);

/** Past this, a fix is history, not a position. */
const STALE_MS = 4 * 60 * 1000;

interface LatLng { lat: number; lng: number }

interface LiveState {
  loading: boolean;
  routeStatus: string;
  routeType: string;
  startedAt: Date | null;
  live: LatLng | null;
  liveAt: number | null;
  trail: LatLng[];
  nickname: string;
  plate: string;
}

const EMPTY: LiveState = {
  loading: true, routeStatus: '', routeType: '', startedAt: null,
  live: null, liveAt: null, trail: [], nickname: '', plate: '',
};

function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (typeof (v as { toDate?: () => Date }).toDate === 'function') return (v as { toDate: () => Date }).toDate();
  return null;
}

function toMillis(v: unknown): number | null {
  if (!v) return null;
  if (typeof (v as { toMillis?: () => number }).toMillis === 'function') {
    return (v as { toMillis: () => number }).toMillis();
  }
  return null;
}

function pt(v: unknown): LatLng | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const lat = Number(o.lat); const lng = Number(o.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
}

function ago(ms: number): string {
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins === 1) return 'a minute ago';
  if (mins < 60) return `${mins} minutes ago`;
  const h = Math.floor(mins / 60);
  return h === 1 ? 'an hour ago' : `${h} hours ago`;
}

const Panel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section style={{
    background: '#fff', border: `1px solid ${FAM.line}`, borderRadius: 20,
    padding: 'clamp(18px,3vw,26px)',
  }}>
    {children}
  </section>
);

interface Props { busId: string }

export default function FamilyLiveRun({ busId }: Props) {
  const [s, setS] = useState<LiveState>(EMPTY);
  /* Re-render on a timer so "last seen 3 minutes ago" does not sit frozen at
     the moment the fix arrived. Cheap: no reads, just a clock. */
  const [, setTick] = useState(0);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '';
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!busId) { setS({ ...EMPTY, loading: false }); return; }
    let alive = true;
    const unsub = onSnapshot(doc(db, 'buses', busId), async (snap) => {
      const raw = snap.data() as Record<string, unknown> | undefined;
      if (!raw) { if (alive) setS({ ...EMPTY, loading: false }); return; }
      let data = raw;
      try {
        data = await materializeBusDoc(raw);
      } catch {
        /* An unopenable pin is a missing pin, never a crash — the panel falls
           back to "we cannot see it", which is the honest reading anyway. */
      }
      if (!alive) return;
      const route = (data.currentRoute || {}) as Record<string, unknown>;
      const live = (data.liveLocation || {}) as Record<string, unknown>;
      const trail = Array.isArray(data.routePoints)
        ? (data.routePoints as unknown[]).map(pt).filter((p): p is LatLng => p !== null)
        : [];
      setS({
        loading: false,
        routeStatus: String(route.status || ''),
        routeType: String(route.type || ''),
        startedAt: toDate(route.startedAt),
        live: pt(live),
        liveAt: toMillis(data.liveLocationUpdatedAt) ?? toMillis(live.timestamp),
        trail,
        nickname: String(data.nickname || ''),
        plate: String(data.plate || busId),
      });
    }, () => { if (alive) setS({ ...EMPTY, loading: false }); });
    return () => { alive = false; unsub(); };
  }, [busId]);

  const open = OPEN_STATUSES.has(s.routeStatus);
  const fixAge = s.liveAt ? Date.now() - s.liveAt : null;
  const stale = fixAge == null || fixAge > STALE_MS;

  const center = useMemo(
    () => s.live ?? s.trail[s.trail.length - 1] ?? null,
    [s.live, s.trail],
  );

  const carLabel = s.nickname || s.plate || 'the car';
  const legWord = s.routeType === 'evening' ? 'afternoon' : 'morning';

  if (s.loading) {
    return <Panel><p style={{ margin: 0, color: FAM.ink2, fontSize: 14.5 }}>Checking the car…</p></Panel>;
  }

  /* ── parked: no map, on purpose ──────────────────────────────────────── */
  if (!open) {
    return (
      <Panel>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 11px', borderRadius: 999,
          background: '#EFEDE7', color: FAM.ink3, fontSize: 12, fontWeight: 700,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: FAM.muted }} />
          Not on the road
        </span>
        <h3 style={{ margin: '12px 0 0', fontSize: 18, fontWeight: 800, color: FAM.ink }}>
          No run is happening right now
        </h3>
        <p style={{ margin: '8px 0 0', fontSize: 14.5, lineHeight: 1.6, color: FAM.ink2, maxWidth: 540 }}>
          The map appears here the moment {carLabel} sets off on a school run, and follows it until it
          arrives. We do not show a map of a parked car — a stationary dot tells you nothing except
          that the car is where you left it.
        </p>
      </Panel>
    );
  }

  /* ── on the road ─────────────────────────────────────────────────────── */
  return (
    <Panel>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 11px', borderRadius: 999,
          background: stale ? FAM.amberTint : FAM.posTint,
          color: stale ? FAM.amber : FAM.pos,
          fontSize: 12, fontWeight: 700,
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: stale ? FAM.amber : FAM.pos2,
          }} />
          {stale ? 'Signal lost' : 'On the road'}
        </span>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: FAM.ink, flex: '1 1 auto' }}>
          The {legWord} run is out
        </h3>
        {s.startedAt && (
          <span style={{ fontSize: 13, color: FAM.ink2 }}>
            left at {s.startedAt.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', hour12: false })}
          </span>
        )}
      </div>

      {/* The honest line about the fix, always present while a run is open. */}
      <p style={{ margin: '10px 0 0', fontSize: 13.5, color: stale ? FAM.amber : FAM.ink2, lineHeight: 1.55 }}>
        {fixAge == null
          ? `The run is open, but ${carLabel} has not sent a position yet. This is usually the phone still finding GPS.`
          : stale
            ? `Last seen ${ago(fixAge)}. The run has not been closed, so this is most likely the driver's phone losing signal — the position below is where it was then, not where it is now.`
            : `Position updated ${ago(fixAge)}.`}
      </p>

      {center && isLoaded && (
        <div style={{ marginTop: 16, borderRadius: 16, overflow: 'hidden', border: `1px solid ${FAM.line}` }}>
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: 320 }}
            center={center}
            zoom={15}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              clickableIcons: false,
            }}
          >
            {s.trail.length >= 2 && (
              <Polyline
                path={s.trail}
                options={{ strokeColor: FAM.accent, strokeOpacity: 0.85, strokeWeight: 4 }}
              />
            )}
            {s.live && (
              <Marker
                position={s.live}
                /* A stale fix is drawn hollow, so the map itself carries the
                   same caveat the sentence above does. */
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 9,
                  fillColor: stale ? '#ffffff' : FAM.accent,
                  fillOpacity: 1,
                  strokeColor: stale ? FAM.amber : '#ffffff',
                  strokeWeight: 3,
                }}
              />
            )}
          </GoogleMap>
        </div>
      )}

      {center && !isLoaded && (
        <p style={{ margin: '14px 0 0', fontSize: 13.5, color: FAM.muted }}>Loading the map…</p>
      )}

      {!center && (
        <p style={{
          margin: '14px 0 0', padding: '11px 14px', borderRadius: 12,
          background: FAM.amberTint, color: FAM.amber, fontSize: 13.5, lineHeight: 1.55,
        }}>
          No position has come through for this run yet, so there is nothing to draw. The run is still
          open — it will appear as soon as the phone reports in.
        </p>
      )}
    </Panel>
  );
}
